/**
 * FreshnessBadge — how recent a reading is, as a coloured dot + Italian label.
 *
 * Colour encodes the freshness bucket but never carries meaning alone: the
 * label text states the age, and the dot has an aria-label (WCAG 2.1 AA).
 */

import { relativeTime } from "@/lib/time";

interface FreshnessBadgeProps {
  /** ISO-8601 UTC instant of the reading, or `null` when there is no data. */
  iso: string | null;
  /** Injectable clock for deterministic tests; defaults to the real now. */
  now?: Date | undefined;
}

const FRESHNESS_TITLE: Record<string, string> = {
  fresh: "Dato aggiornato",
  stale: "Dato in ritardo",
  down: "Dato non aggiornato",
};

export function FreshnessBadge({ iso, now }: FreshnessBadgeProps) {
  if (iso === null) {
    return (
      <span className="freshness freshness--down" title="Nessun dato">
        <span className="freshness__dot" aria-hidden="true" />
        <span className="freshness__label">nessun dato</span>
      </span>
    );
  }

  const { label, freshness } = relativeTime(iso, now ?? new Date());

  return (
    <span
      className={`freshness freshness--${freshness}`}
      title={FRESHNESS_TITLE[freshness]}
      aria-label={`${FRESHNESS_TITLE[freshness]}: ${label}`}
    >
      <span className="freshness__dot" aria-hidden="true" />
      <span className="freshness__label">{label}</span>
    </span>
  );
}
